import { onBeforeUnmount, onMounted, ref, watch } from "vue"

const STORAGE_KEY = "safy.sidebar.collapsed"

// Module-level so every consumer shares one state, and the choice survives reloads.
const collapsed = ref(readStored())

function readStored(): boolean {
  try {
    return localStorage.getItem(STORAGE_KEY) === "1"
  } catch {
    return false
  }
}

watch(collapsed, (value) => {
  try {
    localStorage.setItem(STORAGE_KEY, value ? "1" : "0")
  } catch {
    // Private mode / blocked storage: the toggle still works for this session.
  }
})

const isApple = /mac|iphone|ipad/i.test(navigator.platform || navigator.userAgent)
const shortcutHint = isApple ? "\u2318B" : "Ctrl+B"

// Cmd/Ctrl+B is the browser's bold shortcut inside editable fields, so the
// sidebar only claims it when the user is not typing.
function isTyping(target: EventTarget | null): boolean {
  const element = target as HTMLElement | null
  if (!element)
    return false
  return element.isContentEditable || /^(?:input|textarea|select)$/i.test(element.tagName)
}

export function useSidebarCollapse() {
  function toggle(): void {
    collapsed.value = !collapsed.value
  }

  function onKeydown(event: KeyboardEvent): void {
    if (event.isComposing || event.repeat || isTyping(event.target))
      return
    if (event.key.toLowerCase() !== "b" || !(event.metaKey || event.ctrlKey))
      return
    event.preventDefault()
    toggle()
  }

  onMounted(() => window.addEventListener("keydown", onKeydown))
  onBeforeUnmount(() => window.removeEventListener("keydown", onKeydown))

  return { collapsed, toggle, shortcutHint }
}
